import React, { useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  InputNumber,
  Modal,
  Result,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Card from 'components/card';
import EntryPacket from 'routes/app/arrivePacket';
import SITELINKS from 'constants/sitelinks';
import {
  COLLECTION_STATUS_COLORS,
  COLLECTION_STATUS_LABELS,
  CollectionsApiError,
  useCollection,
  useFulfillCollection,
  useIngestCode,
} from 'services/CollectionsV2Service';

const { Paragraph, Text } = Typography;

const INGEST_ERRORS: Record<string, string> = {
  duplicateCode: 'Ese bulto ya estaba anotado en esta colecta.',
  invalidStatus: 'La colecta todavía no fue retirada, no se puede ingresar.',
};

interface Props {
  collectionId: number;
  onReset: () => void;
  showBack: boolean;
}

const CollectionV2Intake: React.FC<Props> = ({
  collectionId,
  onReset,
  showBack,
}: Props) => {
  const { token } = theme.useToken();
  const [modal, modalHolder] = Modal.useModal();
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [receivedInput, setReceivedInput] = useState<number | null>(null);

  const { data: collection, isLoading, isError } = useCollection(collectionId);
  const { mutateAsync: ingestCode } = useIngestCode();
  const { mutateAsync: fulfill, isPending: isFulfilling } =
    useFulfillCollection();

  const submitCode = async (raw: string) => {
    const externalCode = raw.trim();
    if (!externalCode) return;
    setIngestError(null);
    try {
      await ingestCode({ id: collectionId, externalCode });
    } catch (err) {
      const apiError = err as CollectionsApiError;
      setIngestError(
        (apiError.code && INGEST_ERRORS[apiError.code]) ||
          apiError.message ||
          'El bulto se arribó, pero no se pudo anotar en la colecta',
      );
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (isError || !collection) {
    return (
      <Result
        status="warning"
        title={`No se encontró la colecta #${collectionId}`}
        extra={<Button onClick={onReset}>Volver</Button>}
      />
    );
  }

  const alreadyReceived = collection.status === 'received';
  const canScan =
    collection.status === 'collected' || collection.status === 'ingesting';
  const scanned = collection.intakeCodes?.length ?? 0;
  const declared = collection.declaredPackageCount;
  const received = Math.max(receivedInput ?? declared ?? scanned, scanned);

  const askFulfill = () =>
    modal.confirm({
      title: '¿Ingresar la colecta?',
      okText: 'Ingresar',
      cancelText: 'Cancelar',
      content:
        declared != null && received !== declared ? (
          <Alert
            type="warning"
            showIcon
            message={`Vas a ingresar ${received} bultos y el driver declaró ${declared}.`}
            description="No traba: se ingresa igual y la diferencia queda registrada a tu nombre."
          />
        ) : (
          `La colecta queda recibida en el depósito con ${received} bultos.`
        ),
      onOk: async () => {
        try {
          await fulfill({
            id: collectionId,
            receivedPackageCount: received,
          });
          message.success('Colecta ingresada');
        } catch (err: any) {
          message.error(err?.message ?? 'No se pudo ingresar la colecta');
        }
      },
    });

  return (
    <>
      {modalHolder}
      <Card
        title={`Colecta #${collection.id} · ${collection.storeName}`}
        extra={
          <Space>
            <Tag color={COLLECTION_STATUS_COLORS[collection.status]}>
              {COLLECTION_STATUS_LABELS[collection.status]}
            </Tag>
            {showBack && (
              <Tooltip title="Esc">
                <Button icon={<ArrowLeftOutlined />} onClick={onReset}>
                  Volver
                </Button>
              </Tooltip>
            )}
            {!alreadyReceived && (
              <Button
                type="primary"
                loading={isFulfilling}
                onClick={askFulfill}
              >
                Ingresar colecta
              </Button>
            )}
          </Space>
        }
      >
        <Row gutter={[24, 24]} align="top">
          <Col xs={24} lg={15}>
            <Descriptions
              column={1}
              colon={false}
              labelStyle={{ width: 110, color: token.colorTextSecondary }}
              contentStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Marketplace">
                {collection.marketplaceName}
              </Descriptions.Item>
              <Descriptions.Item label="Dirección">
                {collection.address}
              </Descriptions.Item>
              <Descriptions.Item label="Retiró">
                {collection.driverName ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Patente">
                {collection.vehiclePlate ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ruta">
                {collection.journeyId ? (
                  <Link to={`${SITELINKS.journey}/${collection.journeyId}`}>
                    {collection.journeyId}
                  </Link>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          <Col xs={24} lg={9}>
            <div
              style={{
                background: token.colorFillAlter,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: token.borderRadiusLG,
                padding: 16,
              }}
            >
              <Statistic
                title="Bultos arribados / declarados por el driver"
                value={scanned}
                suffix={`/ ${declared ?? '?'}`}
                valueStyle={{ color: token.colorPrimary, fontSize: 40 }}
              />
              <Divider style={{ margin: '12px 0' }} />
              {alreadyReceived ? (
                <Statistic
                  title="Recibidos"
                  value={collection.receivedPackageCount ?? '-'}
                />
              ) : (
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text strong>Bultos recibidos</Text>
                  <InputNumber
                    min={scanned}
                    precision={0}
                    size="large"
                    value={received}
                    style={{ width: '100%' }}
                    onChange={setReceivedInput}
                  />
                  <Text type="secondary">
                    Si faltan bultos, poné los que llegaron: no hace falta
                    escanearlos.
                  </Text>
                </Space>
              )}
              {scanned > 0 && (
                <>
                  <Divider style={{ margin: '12px 0' }} />
                  <Space size={[4, 8]} wrap>
                    {collection.intakeCodes.map((intake) => (
                      <Tag color="green" key={intake.externalCode}>
                        {intake.externalCode}
                      </Tag>
                    ))}
                  </Space>
                </>
              )}
            </div>
          </Col>
        </Row>

        {canScan && (
          <>
            <Divider />
            <Paragraph style={{ marginBottom: 12 }}>
              <Text strong>Arribar bultos</Text>{' '}
              <Text type="secondary">
                (opcional: se puede ingresar sin escanear ningún bulto)
              </Text>
            </Paragraph>
            <Alert
              type="info"
              showIcon
              message="Cada bulto que escaneás se arriba en el sistema y queda anotado en esta colecta."
              style={{ marginBottom: 12 }}
            />
            <EntryPacket onSuccess={submitCode} />
            {ingestError && (
              <Alert
                type="error"
                showIcon
                message={ingestError}
                style={{ marginTop: 12 }}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
};

export default CollectionV2Intake;
